import React from "react";

import { SkeletonText } from "~/components/skeleton/text";
import { Table } from "~/components/table";
import { TableBody } from "~/components/table/body";
import { TableCell } from "~/components/table/cell";
import { TableColFilter } from "~/components/table/col-filter";
import { TableColOrder } from "~/components/table/col-order";
import { TableHead } from "~/components/table/head";
import { TableHeadCell } from "~/components/table/head-cell";
import { TableHeadCellFilter } from "~/components/table/head-cell-filter";
import { TableHeadCellSorter } from "~/components/table/head-cell-sorter";
import { TableRow } from "~/components/table/row";
import { toDate } from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";
import { type Impulse, useScoped } from "~/tools/impulse";
import type { Record } from "~/tools/record";
import { sortBy } from "~/tools/sort-by";

import type { Api, HealthStateValue } from "../../common/openapi";
import { healthStateToStateWeight } from "../../generic/Util";
import { queryMonitorCheckStates, queryTestMonitorCheckStates } from "../../queries/monitors";
import { useQuery } from "../../queries/react-query";
import { COMPONENT_PATH } from "../../routes/Routes";
import { useClosestScrollingContainer } from "../_scrolling-container";
import { HealthStateBadge } from "../badges/HealthStateBadge";
import { ComponentLink } from "../react/component-link/ComponentLink";
import { GenericErrorMessage } from "../react/error/GenericErrorMessage";
import { LinkTo } from "../react/link";
import { FormatViewTime } from "../react/viewtime/FormatViewTime";

import type { MonitorConfig } from "./monitor-output-drawer-state";

export interface MonitorOutputDrawerTableProps {
	orders: Record<"healthState" | "componentName" | "componentType" | "lastUpdateTimestamp", Impulse<TableColOrder>>;
	filters: Record<"componentName" | "componentType", Impulse<TableColFilter>>;
	monitor: MonitorConfig;
	healthState: undefined | HealthStateValue;
	timestamp: undefined | Time;
	count: undefined | number;
}

export const MonitorOutputDrawerTable: React.FC<MonitorOutputDrawerTableProps> = ({
	orders,
	filters,
	monitor,
	healthState,
	timestamp,
	count,
}) => {
	const { element } = useClosestScrollingContainer();

	const componentNameFilter = useScoped(filters.componentName);
	const componentTypeFilter = useScoped(filters.componentType);
	const healthStateOrder = useScoped(orders.healthState);
	const componentNameOrder = useScoped(orders.componentName);
	const componentTypeOrder = useScoped(orders.componentType);
	const lastUpdateTimestampOrder = useScoped(orders.lastUpdateTimestamp);

	const select = React.useCallback(
		(checkStates: ReadonlyArray<Api.ViewCheckState>) => {
			const filtered = TableColFilter.filterBy(
				checkStates,
				[
					({ componentName = "" }, query) => componentName.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
					componentNameFilter,
				],
				[
					({ componentType = "" }, query) => componentType.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
					componentTypeFilter,
				],
			);

			return TableColOrder.orderBy(
				// presort by health state
				sortBy(filtered, ({ healthState }) => healthStateToStateWeight(healthState)),
				[({ healthState }) => healthStateToStateWeight(healthState), healthStateOrder],
				[({ componentName = "" }) => componentName, componentNameOrder],
				[({ componentType = "" }) => componentType, componentTypeOrder],
				[({ lastUpdateTimestamp }) => lastUpdateTimestamp, lastUpdateTimestampOrder],
			);
		},
		[
			componentNameFilter,
			componentNameOrder,
			componentTypeFilter,
			componentTypeOrder,
			healthStateOrder,
			lastUpdateTimestampOrder,
		],
	);

	const query =
		monitor.type === "MONITOR"
			? queryMonitorCheckStates(
					{
						monitorIdOrUrn: monitor.monitorId,
						healthState,
						timestamp,
					},
					{ select },
				)
			: queryTestMonitorCheckStates(
					{
						monitorFunctionIdOrUrn: monitor.monitorFunctionId,
						arguments: monitor.arguments,
						healthState,
					},
					{ select },
				);

	const { error, data } = useQuery(query);

	if (error != null) {
		return <GenericErrorMessage error={error} />;
	}

	return (
		<Table className="grid-cols-[120px_minmax(480px,1fr)_minmax(180px,480px)_180px]" scrollElement={element}>
			<TableHead className="sticky top-0 z-10">
				<TableHeadCell>
					Health
					<TableHeadCellSorter onOrderChange={(order) => orders.healthState.setValue(order)} order={healthStateOrder} />
				</TableHeadCell>

				<TableHeadCell>
					<span className="flex-1">Component</span>
					<TableHeadCellSorter
						onOrderChange={(order) => orders.componentName.setValue(order)}
						order={componentNameOrder}
					/>
					<TableHeadCellFilter
						filter={componentNameFilter}
						onFilterChange={(filter) => filters.componentName.setValue(filter)}
					/>
				</TableHeadCell>

				<TableHeadCell>
					<span className="flex-1">Type</span>
					<TableHeadCellSorter
						onOrderChange={(order) => orders.componentType.setValue(order)}
						order={componentTypeOrder}
					/>
					<TableHeadCellFilter
						filter={componentTypeFilter}
						onFilterChange={(filter) => filters.componentType.setValue(filter)}
					/>
				</TableHeadCell>

				<TableHeadCell className="justify-end">
					Updated on
					<TableHeadCellSorter
						onOrderChange={(order) => orders.lastUpdateTimestamp.setValue(order)}
						order={lastUpdateTimestampOrder}
					/>
				</TableHeadCell>
			</TableHead>

			<TableBody
				each={data}
				remaining={data == null ? (count ?? 50) : 0}
				virtualize={{
					estimateSize: 41,
					overscan: 1,
				}}
			>
				{(checkState) => (
					<TableRow className="relative z-0">
						<TableCell className="justify-center">
							<HealthStateBadge state={checkState?.healthState ?? null} />
						</TableCell>

						<TableCell>
							<SkeletonText className="flex-1" el="div" randomize when={checkState}>
								{({ componentName, componentIdentifier }) => {
									if (!componentName) {
										return "-";
									}

									return (
										<ComponentLink
											componentIdentifier={componentIdentifier}
											search={{
												timestamp: toDate(timestamp),
											}}
											text={componentName!}
										/>
									);
								}}
							</SkeletonText>
						</TableCell>

						<TableCell>
							<SkeletonText
								className="flex-1"
								el="div"
								randomize
								when={checkState ? (checkState.componentType ?? "-") : null}
							/>
						</TableCell>

						<TableCell className="justify-end text-right">
							<SkeletonText className="flex-1" el="div" randomize when={checkState}>
								{({ componentIdentifier, lastUpdateTimestamp }) => (
									<LinkTo
										className="whitespace-nowrap underline hover:no-underline"
										params={{ identifier: componentIdentifier }}
										search={(search) => ({
											...search,
											query: undefined,
											explore: undefined,
											timestamp: toDate(lastUpdateTimestamp),
										})}
										to={COMPONENT_PATH}
									>
										<FormatViewTime time={lastUpdateTimestamp} />
									</LinkTo>
								)}
							</SkeletonText>
						</TableCell>
					</TableRow>
				)}
			</TableBody>
		</Table>
	);
};
